/* eslint-disable no-param-reassign */
import {
  createAsyncThunk,
  createSlice,
  DeepPartial,
  PayloadAction,
} from "@reduxjs/toolkit"
import { addDays, differenceInDays, format, parseISO } from "date-fns"

import { GetPrecisionPricingStatusRequest } from "commonModule/services/booking/precisionPricing"
import { PromoCodeRequest } from "commonModule/services/booking/promoCode"
import { PromoCodeDetailsRequest } from "commonModule/services/booking/promoCodeDetail"
import { apiBookingClient } from "services/apiBookingClient"
import { apiClientCommon } from "services/apiClientCommon"
import { RootState } from "stores/store"
import {
  getDetailPageOrigin,
  getPropertyPriceForDetailPageEvent,
  removeDetailPageOrigin,
  removePropertyPriceForDetailPageEvent,
} from "utils/local-storage"
import { markStates } from "utils/misc"

import { calculateDiscount } from "./helpers"
import { BookingInitialState, PrecisionPricing } from "./types"

export const loadPrequote = createAsyncThunk(
  "booking/loadPrequote",
  async (
    request: {
      preQuoteFlag: boolean
      propertyId: string | null
      isRequestFromHost: boolean
      pmcId: string
      pmsSystem: string
      isgoogle: boolean
      dayCount?: number
      petNumber?: number
      pet?: boolean
      startDate?: string
      endDate?: string
      adultNumber?: number
      childNumber?: number
      prequoteRequestId?: string
      searchPrice?: string
      allInPriceToggle?: boolean
      directIncentive?: boolean
      quoteId?: string
      allowSameDay?: boolean
      commissionRatePercent?: number | null
    },
    thunkAPI,
  ) => {
    const bookingStore = (thunkAPI.getState() as RootState).bookingReducer
    if (
      !bookingStore.startDate ||
      !bookingStore.endDate ||
      !request.propertyId
    ) {
      throw Error("Invalid Preqoute Request Sent")
    }
    try {
      const response = await apiBookingClient.getPrequote({
        params: {
          adultNumber: request.adultNumber
            ? request.adultNumber
            : bookingStore.adultNumber || 2,
          childNumber: request.childNumber
            ? request.childNumber
            : bookingStore.childNumber || 0,
          dayCount: request.dayCount
            ? request.dayCount
            : bookingStore.dayCount || 1,
          startDate: request.startDate
            ? request.startDate
            : bookingStore.startDate,
          endDate: request.endDate ? request.endDate : bookingStore.endDate,
          pet: request.pet ? request.pet : bookingStore.pet || false,
          petNumber: request.petNumber
            ? request.petNumber
            : bookingStore.petNumber || null,
          propertyId: request.propertyId,
          preQuoteFlag: request.preQuoteFlag,
          isRequestFromHost: request.isRequestFromHost || false,
          isgoogle: request.isgoogle || false,
          pmcId: request.pmcId,
          pmsSystem: request.pmsSystem,
          prequoteRequestId: request.prequoteRequestId || null,
          searchPrice: request.searchPrice || null,
          allInPriceToggle: request.allInPriceToggle || false,
          directIncentive: request.directIncentive || false,
          quoteId: request.quoteId || null,
          commissionRatePercent:
            request.commissionRatePercent != null
              ? request.commissionRatePercent
              : bookingStore.commissionRatePercent ?? null,
        },
      })
      return response.data
    } catch (error) {
      const axiosError = error as {
        response?: { status?: number; data?: { message?: string } }
        message?: string
      }
      return thunkAPI.rejectWithValue({
        status: axiosError.response?.status,
        message: axiosError.response?.data?.message || axiosError.message,
      })
    }
  },
)

export const getPropertyPrecisionPricingStatus = createAsyncThunk(
  "booking/getPropertyPrecisionPricingStatus",
  async (request: GetPrecisionPricingStatusRequest) => {
    const response = await apiBookingClient.getPrecisionPricingStatus(request)
    return response.data
  },
)

export const promoCode = createAsyncThunk(
  "booking/promoCode",
  async (request: PromoCodeRequest, thunkAPI) => {
    try {
      const response = await apiClientCommon.getPromoCode(request)
      return response.data
    } catch (error) {
      const axiosError = error as {
        response?: { data?: { message?: string }; status?: number }
        message?: string
      }

      return thunkAPI.rejectWithValue({
        message: axiosError.response?.data?.message || axiosError.message,
        status: axiosError.response?.status,
      })
    }
  },
)

export const promoCodeDetails = createAsyncThunk(
  "booking/promoCodeDetails",
  async (request: PromoCodeDetailsRequest, thunkAPI) => {
    try {
      const response = await apiClientCommon.promoCodeDetails(request)
      return response.data
    } catch (error) {
      const axiosError = error as {
        response?: { data?: { message?: string }; status?: number }
        message?: string
      }

      return thunkAPI.rejectWithValue({
        message: axiosError.response?.data?.message || axiosError.message,
        status: axiosError.response?.status,
      })
    }
  },
)

export const refundable = createAsyncThunk("booking/refundable", async () => {
  const response = await apiBookingClient.applyRefundable()
  return response.data
})

export const getTravellerAddress = createAsyncThunk(
  "booking/getTravellerAddress",
  async () => {
    const response = await apiBookingClient.getTravellerAddress()
    return response.data
  },
)

export const registerClickDateChangeEventMixPanel = createAsyncThunk(
  "booking/registerClickDateChangeEventMixPanel",
  async (
    actions: {
      city: string
      property_id: string
      property_rating_star_sum?: number | null
      property_rating_user_count?: number | null
      state: string
      isMultiUnit?: boolean // to check user comes from multi units detail page or not
      guestCount?: number
      cancellationPolicy?: string
    },
    thunkAPI,
  ) => {
    const state = (thunkAPI.getState() as RootState).bookingReducer
    const mixpanelV2Client = (await import("services/mixpanelv2"))
      .mixpanelV2Client

    mixpanelV2Client.CLICK_CHANGE_DATES({
      propertyId: actions.property_id,
      city: actions.city,
      state: actions.state,
      totalNights: state.dayCount,
      numOfDaysUntilCheckIn: differenceInDays(
        parseISO(state.startDate || ""),
        parseISO(new Date().toJSON().slice(0, 10)),
      ),
      totalAmount: parseFloat(
        (
          Number(state?.totalAmt) +
          (state.prequote?.pdpFees || 0) +
          Number(state?.discount)
        )?.toFixed(2),
      ),
      hostFee: state.prequote?.cleaningFee || 0,
      totalServiceFee:
        (state.prequote?.originalServiceFee || 0) +
        (state.prequote?.pdpFees || 0),
      totalTax: state.prequote?.totalTax || 0,
      totalSaving: state.prequote?.savedAmount?.toFixed(2) || 0,
      propertyRatingStarSum: actions.property_rating_star_sum,
      propertyRatingUserCount: actions.property_rating_user_count,
      isMultiUnit: actions.isMultiUnit || false,
      guestCount: actions.guestCount || 0,
      cancellation_policy: actions.cancellationPolicy,
    })
  },
)

export const registerViewFinalPaymentPageEventMixPanel = createAsyncThunk(
  "booking/registerViewFinalPaymentPageEventMixPanel",
  async (
    actions: {
      city: string
      property_id: string
      property_rating_star_sum?: number | null
      property_rating_user_count?: number | null
      state: string
      isMultiUnit?: boolean // to check user comes from multi units detail page or not
      guestCount?: number
      paymentOption: string
      optIn?: string
      theme?: string
      isLoggedIn?: boolean
      version?: string
    },
    thunkAPI,
  ) => {
    const state = (thunkAPI.getState() as RootState).bookingReducer
    const mixpanelV2Client = (await import("services/mixpanelv2"))
      .mixpanelV2Client

    mixpanelV2Client.VIEW_CHECKOUT_PAYMENT_PAGE({
      propertyId: actions.property_id,
      city: actions.city,
      state: actions.state,
      totalNights: state.dayCount,
      numOfDaysUntilCheckIn: differenceInDays(
        parseISO(state.startDate || ""),
        parseISO(new Date().toJSON().slice(0, 10)),
      ),
      totalAmount: parseFloat(
        (
          Number(state?.totalAmt) +
          (state.prequote?.pdpFees || 0) +
          Number(state?.discount)
        )?.toFixed(2),
      ),
      hostFee: state.prequote?.cleaningFee || 0,
      totalServiceFee:
        (state.prequote?.originalServiceFee || 0) +
        (state.prequote?.pdpFees || 0),
      totalTax: state.prequote?.totalTax || 0,
      totalSaving: state.prequote?.savedAmount?.toFixed(2) || 0,
      propertyRatingStarSum: actions.property_rating_star_sum,
      propertyRatingUserCount: actions.property_rating_user_count,
      isMultiUnit: actions.isMultiUnit || false,
      guestCount: actions.guestCount || 0,
      paymentOption: actions.paymentOption,
      optIn: actions.optIn,
      theme: actions.theme,
      isLoggedIn: actions.isLoggedIn,
      version: actions.version,
    })
  },
)

export const registerViewDetailPageEventMixpanel = createAsyncThunk(
  "booking/registerViewDetailPageEventMixpanel",
  async (
    actions: {
      city: string
      excludingTaxesTotalAmount: number
      max_price: number
      min_price: number
      no_of_bedrooms: number
      property_id: string
      property_rating_star_sum?: number | null
      property_rating_user_count?: number | null
      property_type_desc: string
      state: string
      description_variant: string
      country?: string
      actual_price?: string
      saved_price?: string
      strike_price?: string
      featured?: boolean
      aiSummaryShown?: boolean
      savingsAmountBasedOnPrecisionPricing?: string
      isPPShown?: boolean
      source?: string | null
      highlightsShown?: boolean
      airbnbAmenitiesEnhanced?: boolean
      selfCheckInAvailable?: "Yes" | "No"
      isMultiUnit?: boolean
      muPDPFlow?: "direct_to_pdp" | "via_mu_detail"
      crossSellVersion?: "New" | "Old"
      preferredHost?: boolean
    },
    thunkAPI,
  ) => {
    const state = (thunkAPI.getState() as RootState).bookingReducer

    // so mixpanel won't trigger until isPreqouteLoaded
    if (!state.isLoading && state.isPreqouteLoaded) {
      const pageOrigin = getDetailPageOrigin()
      const priceShowingInPropertyCard =
        getPropertyPriceForDetailPageEvent() || "NA"

      removePropertyPriceForDetailPageEvent()
      removeDetailPageOrigin()

      const whimstayTotal = state?.totalAmt
        ? state?.totalAmt + (state.prequote?.pdpFees || 0)
        : 0
      const totalBeforeTaxesAirbnb =
        state.precisionPricing?.totalBeforeTaxesAirbnb
      const totalBeforeTaxesVrbo = state.precisionPricing?.totalBeforeTaxesVrbo

      const mixpanelV2Client = (await import("services/mixpanelv2"))
        .mixpanelV2Client

      mixpanelV2Client.VIEW_PROPERTY_DETAILS_PAGE({
        propertyId: actions.property_id,
        city: actions.city,
        state: actions.state,
        dates:
          state.startDate || state.endDate
            ? `${state.startDate || ""}, ${state.endDate || ""}`
            : "NA",
        totalNights: state.startDate && state.endDate ? state?.dayCount : "NA",
        numOfDaysUntilCheckIn: state.startDate
          ? differenceInDays(
              parseISO(state.startDate),
              parseISO(new Date().toJSON().slice(0, 10)),
            )
          : "NA",
        totalAmount: actions.excludingTaxesTotalAmount,
        hostFee:
          state.prequote?.cleaningFee && state.prequote.cleaningFee > 0
            ? state.prequote.cleaningFee.toFixed(2)
            : state?.hasPrequote
            ? 0
            : "NA",
        totalServiceFee:
          state.prequote?.originalServiceFee &&
          state.prequote?.originalServiceFee > 0
            ? state.prequote?.originalServiceFee +
              (state.prequote?.pdpFees || 0)
            : state?.hasPrequote
            ? 0
            : "NA",
        totalSaving: state?.hasPrequote
          ? state.prequote?.savedAmount && state.prequote?.savedAmount > 0
            ? state.prequote?.savedAmount?.toFixed(2)
            : "NA"
          : actions.max_price && actions.min_price
          ? Math.round(actions.max_price) - Math.round(actions.min_price)
          : "NA",
        propertyTypeDesc: actions.property_type_desc,
        numberOfBedroom: actions.no_of_bedrooms,
        pageOrigin: actions.source
          ? actions.source
          : pageOrigin
          ? pageOrigin
          : "open via link",
        propertyRatingStarSum: actions.property_rating_star_sum,
        propertyRatingUserCount: actions.property_rating_user_count,
        totalDisplayPriceExcludingTaxes: priceShowingInPropertyCard,
        description_variant: actions.description_variant,
        actual_price: actions.actual_price || "NA",
        saved_price: actions.saved_price || "NA",
        strike_price: actions.strike_price || "NA",
        featured: actions.featured || false,
        aiSummaryShown: actions.aiSummaryShown || false,
        savingsAmountBasedOnPrecisionPricing:
          actions.savingsAmountBasedOnPrecisionPricing || undefined,
        isPPShown: actions.isPPShown,
        priceDifference: state.prequote?.priceDifference || null,
        totalDisplayPriceWithTaxes:
          (state?.totalAmt &&
            state?.totalAmt + (state.prequote?.pdpFees || 0)) ||
          null,
        availabilityError:
          state.startDate && state.endDate ? !state.hasPrequote : false,
        highlightsShown: actions.highlightsShown || false,
        airbnbAmenitiesEnhanced: actions.airbnbAmenitiesEnhanced || false,
        selfCheckInAvailable: actions.selfCheckInAvailable || "No",
        airbnbSavingsPercent: totalBeforeTaxesAirbnb
          ? (totalBeforeTaxesAirbnb - whimstayTotal) / whimstayTotal
          : 0,
        vrboSavingsPercent: totalBeforeTaxesVrbo
          ? (totalBeforeTaxesVrbo - whimstayTotal) / whimstayTotal
          : 0,
        isMultiUnit: actions.isMultiUnit || false,
        muPDPFlow: actions.muPDPFlow,
        crossSellVersion: actions.crossSellVersion,
        preferredHost: actions.preferredHost,
      })
    }
  },
)

const initialState: BookingInitialState = {
  startDate: null,
  endDate: null,
  dayCount: 0,
  adultNumber: 2,
  childNumber: 0,
  propertyId: null,
  error: null,
  status: null,
  isLoading: false,
  prequote: { damageDeposit: 0 },
  hasPrequote: false,
  bookingAmount: 0,
  isAffirmPayment: false,
  isKlarnaPayment: false,
  isGooglePayment: false,
  isApplePayAvailable: false,
  totalAmt: 0,
  buttonText: "Check Availability",
  promocode: null,
  isCalendarOpen: false,
  isDateAvailable: false,
  calendarRate: null,
  calendarRateList: [],
  maximizeProfit: true,
  pet: false,
  petNumber: 0,
  commissionRatePercent: null,
  discount: 0,
  isTravelProtected: false,
  isCancelBookingTravelProtected: false,
  refundable: null,
  affirmFeePercentage: 0,
  CFAR: null,
  TI: null,
  cfarDays: null,
  travellerInfo: null,
  initialTravellerInfo: null,
  clientSecret: "",
  bookingNo: "",
  bookingPayload: null,
  isPaymentPage: false,

  calendarData: {
    averagePrice: 100,
    checkArrivalDeparture: false,
  },
  isPreqouteLoaded: true,
  isPrecisionPricingProcessComplete: false,
  precisionPricing: {
    isPrecisionPricingLoading: false,
    precisionPricingError: null,
    airbnbUrl: null,
    totalBeforeTaxesAirbnb: 0,
    vrboUrl: null,
    totalBeforeTaxesVrbo: 0,
    precisionPricingLoaded: false,
    precisionPricingUuid: "",
    vrboAvailable: null,
    airbnbAvailable: null,
    trustPilot: null,
    isAirbnbLoading: false,
    isVrboLoading: false,
    isAirbnbEstimatedPrice: false,
    isVrboEstimatedPrice: false,
    airbnbError: null,
    vrboError: null,
    exclusiveOnWhimstay: false,
    whimstayExclusive: null,
    precisionPricingStatus: {
      ota: null,
      pp: null,
    },
    // pp: null,
    // ota: null,
  },
  achProcessingSavings: null,
  precisionPricingAllowedDays: 29, // default to 29 days
  maxCancellationFee: 0,
  affirmFee: 0,
  klarnaFee: 0,
  isSendBookingConfrimationTOClient: false,
  isSendBookingConfrimationTOClientWithPrice: false,
}

export const bookingSlice = createSlice({
  name: "booking",
  initialState,
  reducers: {
    resetBookingStore: (state) => {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      state = initialState
    },
    markBookingStore: (
      state,
      action: PayloadAction<DeepPartial<BookingInitialState>>,
    ) => {
      markStates(action.payload, state)
      if (state.endDate && state.startDate) {
        state.dayCount = differenceInDays(
          parseISO(state.endDate),
          parseISO(state.startDate),
        )
      } else {
        state.dayCount = 0
      }
    },

    clearPrecisionPricingData: (state) => {
      state.precisionPricing = {
        ...state.precisionPricing,
        isPrecisionPricingLoading: false,
        precisionPricingError: null,
        airbnbUrl: null,
        totalBeforeTaxesAirbnb: 0,
        vrboUrl: null,
        totalBeforeTaxesVrbo: 0,
        precisionPricingLoaded: false,
        exclusiveOnWhimstay: false,
        vrboAvailable: null,
        airbnbAvailable: null,
        whimstayExclusive: null,
        isAirbnbDisplayable: false,
        isVrboDisplayable: false,
      }
    },
    setPrecisionPrincingData: (
      state,
      action: PayloadAction<Partial<PrecisionPricing>>,
    ) => {
      state.precisionPricing = {
        ...state.precisionPricing,
        ...action.payload,
      }
    },
    setIsPrecisionPricingProcessComplete: (
      state,
      action: PayloadAction<boolean>,
    ) => {
      state.isPrecisionPricingProcessComplete = action.payload
    },
  },
  extraReducers: (builder) => {
    builder.addCase(loadPrequote.pending, (state) => {
      state.isLoading = true
      state.error = null
      state.isPreqouteLoaded = false
    })
    builder.addCase(loadPrequote.fulfilled, (state, action) => {
      state.isPreqouteLoaded = true

      state.prequote = {
        ...action.payload.body,
        originalServiceFee:
          action.payload.body.originalServiceFee +
          (action.payload.body.adjustedAmt || 0),
      }
      state.hasPrequote = action.payload.body?.success || false
      // action.meta.arg.propertyId
      state.prequote.priceChangeMessage = action.payload.body.priceChangeMessage

      // body.rent already includes originalRent + originalServiceFee so adding adjustedAmt to rent
      const rent =
        action.payload.body.rent + (action.payload.body.adjustedAmt || 0)
      const cleaningFee =
        action.payload.body.fees?.reduce((a, c) => a + c.value, 0) || 0
      const serviceFee = action.payload.body.serviceFee
      const totalTax =
        action.payload.body.taxes?.reduce((a, c) => a + c.value, 0) || 0

      let dailyAvgRent = 0
      if (action.payload.body.days.length > 0) {
        dailyAvgRent =
          action.payload.body.days.reduce((a, c) => a + c.dailyRent, 0) /
          action.payload.body.days.length
      }
      // update all fees and rents accordingly
      state.prequote.dailyAvgRent =
        dailyAvgRent + (action.payload.body.taUpliftFee || 0)
      state.prequote.originalRent =
        action.payload.body.originalRent +
        (action.payload.body.taUpliftFee || 0)
      state.prequote.cleaningFee = cleaningFee
      state.prequote.serviceFee = serviceFee
      state.prequote.totalTax = totalTax
      state.prequote.totalAmt =
        rent +
        cleaningFee +
        serviceFee +
        totalTax +
        (state.prequote.damageDeposit || 0)

      state.totalAmt = state.prequote.totalAmt
      markStates(
        calculateDiscount({
          promoCode: state.promocode,
          totalAmt: state.totalAmt,
          pdpFees: state.prequote.pdpFees || 0,
        }),
        state,
      )

      // in booking payload we have to pass booking amount and not total amount which is only amount payable to pmc
      state.bookingAmount =
        (state.prequote.rent || 0) +
        (state.prequote.cleaningFee || 0) +
        (state.prequote.totalTax || 0)
      state.buttonText = "Book Now"
      state.isDateAvailable = true

      state.error = null
      state.isLoading = false

      if (action.meta.arg.pmsSystem === "whimstay") {
        void Promise.resolve()
          .then(async () => {
            const startDate =
              action.payload.body.days && action.payload.body.days?.[0]?.date
            const endDate =
              action.payload.body.days &&
              action.payload.body.days?.[action.payload.body.days?.length - 1]
                ?.date
            const finalEndDate = endDate
              ? format(addDays(parseISO(endDate), 1), "yyyy-MM-dd")
              : null

            const { mixpanelV2Client } = await import("services/mixpanelv2")
            mixpanelV2Client.CLICK_AIRBNB_PREQUOTE({
              propertyId: action.meta.arg.propertyId || "",
              selectedDates:
                startDate || finalEndDate
                  ? `${startDate || ""} , ${finalEndDate || ""}`
                  : "NA",
              cleaningFees: action.payload.body?.cleaningFee,
              totalRent: action.payload.body?.airbnbRent,
              cleaningFeeSource: action.payload.body?.cleaningFeeSource || "",
              origin: "view property detail page",
            })
            return true
          })
          .catch((error) => {
            console.error("Failed to send Mixpanel event:", error)
          })
      }
    })
    builder.addCase(loadPrequote.rejected, (state, action) => {
      state.isPreqouteLoaded = true
      state.isLoading = false

      const payload = action.payload as
        | { status?: number; message: string }
        | undefined
      const status = payload?.status
      const message =
        payload?.message || action.error.message || "Prequote Error"

      state.error = message
      state.status = status || null
      state.hasPrequote = false
      state.bookingAmount = 0
      const tempState: {
        adultNumber: number
        childNumber: number
        pet: boolean | undefined
        startDate: string | null
        dayCount: number
      } = {
        adultNumber: state.adultNumber,
        childNumber: state.childNumber,
        pet: state.pet,
        startDate: state.startDate,
        dayCount: state.dayCount,
      }
      state.startDate = null
      state.endDate = null
      state.dayCount = 0
      state.prequote = { damageDeposit: 0 }
      state.buttonText = "Check Availability"
      state.isDateAvailable = false

      state.pet = false
      state.adultNumber = 1
      state.childNumber = 0

      if (
        message === "Prequote request not valid" ||
        (message?.search("400") || -1) >= 0 ||
        (message?.search("401") || -1) >= 0 ||
        status === 417
      ) {
        void (async (adultNumber, childNumber, pet, startDate, dayCount) => {
          if (startDate && dayCount) {
            const mixpanelV1Client = (await import("services/mixpanel"))
              .mixpanelV1Client
            mixpanelV1Client.BOOKING_STORE_INVALID_PREQUOTE_REQUEST({
              pmcId: action.meta.arg.pmcId || "",
              pmsSystem: action.meta.arg.pmsSystem || "",
              propertyId: action.meta.arg.propertyId || "",
              adultNumber: adultNumber || 1,
              childNumber: childNumber || 0,
              petsAllowed: pet || false,
              startDate: startDate,
              dayCount: dayCount,
              errorMessage: message,
              quoteError: status === 417,
            })
          }
        })(
          tempState.adultNumber,
          tempState.childNumber,
          tempState.pet,
          tempState.startDate,
          tempState.dayCount,
        )
      } else {
        void (async (adultNumber, childNumber, pet, startDate, dayCount) => {
          if (startDate && dayCount) {
            const mixpanelV1Client = (await import("services/mixpanel"))
              .mixpanelV1Client
            mixpanelV1Client.BOOKING_STORE_PREQUOTE_ERROR({
              pmcId: action.meta.arg.pmcId || "",
              pmsSystem: action.meta.arg.pmsSystem || "",
              propertyId: action.meta.arg.propertyId || "",
              adultNumber: adultNumber || 1,
              childNumber: childNumber || 0,
              petsAllowed: pet || false,
              startDate: startDate,
              dayCount: dayCount,
              errorMessage: message,
            })
          }
        })(
          tempState.adultNumber,
          tempState.childNumber,
          tempState.pet,
          tempState.startDate,
          tempState.dayCount,
        )
      }
    })

    builder.addCase(getPropertyPrecisionPricingStatus.pending, (state) => {
      state.precisionPricing.ota = null
      state.precisionPricing.pp = null
    })
    builder.addCase(
      getPropertyPrecisionPricingStatus.fulfilled,
      (state, action) => {
        state.precisionPricing.ota = action.payload.data.ota
        state.precisionPricing.pp = action.payload.data.pp
      },
    )
    builder.addCase(getPropertyPrecisionPricingStatus.rejected, (state) => {
      state.precisionPricing.ota = null
      state.precisionPricing.pp = null
    })
    builder.addCase(promoCode.pending, (state) => {
      state.isLoading = false
      state.error = null
    })
    builder.addCase(promoCode.fulfilled, (state, action) => {
      state.error = null
      state.isLoading = false
      state.promocode = {
        isPercent: action.payload.isPercent,
        value: action.payload.value,
        promoCodeId: action.payload.promoCodeId,
        promoCode: action.payload.promoCode,
      }

      markStates(
        calculateDiscount({
          promoCode: state.promocode,
          totalAmt: state.totalAmt || 0,
          pdpFees: state?.prequote?.pdpFees || 0,
        }),
        state,
      )
    })
    builder.addCase(promoCode.rejected, (state, action) => {
      const payload = action.payload as { message?: string } | undefined
      state.isLoading = false
      state.error =
        payload?.message || action.error.message || "PromoCode Error"
      state.promocode = null

      setTimeout(() => {
        void (async () => {
          const mixpanelV2Client = (await import("services/mixpanelv2"))
            .mixpanelV2Client
          mixpanelV2Client.ENTER_PROMOCODE({
            promocode: action.meta.arg.params.code,
            promocodeApplied: "false",
            promocodeAmount: 0,
          })
        })()
      }, 0)
    })

    builder.addCase(promoCodeDetails.pending, (state) => {
      state.isLoading = false
      state.error = null
    })
    builder.addCase(promoCodeDetails.fulfilled, (state, action) => {
      state.error = null
      state.isLoading = false
      state.promocode = {
        isPercent: action.payload.isPercent,
        value: action.payload.value,
        promoCodeId: action.payload.promoCodeId,
        promoCode: action.payload.promoCode,
      }

      markStates(
        calculateDiscount({
          promoCode: state.promocode,
          totalAmt: state.totalAmt || 0,
          pdpFees: state?.prequote?.pdpFees || 0,
        }),
        state,
      )
    })
    builder.addCase(promoCodeDetails.rejected, (state, action) => {
      const payload = action.payload as { message?: string } | undefined
      state.isLoading = false
      state.error =
        payload?.message || action.error.message || "PromoCode Error"
      state.promocode = null

      setTimeout(() => {
        void (async () => {
          const mixpanelV2Client = (await import("services/mixpanelv2"))
            .mixpanelV2Client
          mixpanelV2Client.ENTER_PROMOCODE({
            promocode: action.meta.arg.params.code,
            promocodeApplied: "false",
            promocodeAmount: 0,
          })
        })()
      }, 0)
    })
    builder.addCase(refundable.fulfilled, (state, action) => {
      state.isLoading = false
      state.affirmFeePercentage = action.payload.AffirmFeePercentage
      state.refundable = action.payload.refundProtectPremiumRate
      state.CFAR = action.payload?.CFAR || "10.8"
      state.TI = action.payload?.TI || "7.0"
      state.cfarDays = action.payload?.cfar_days || "30"
    })
    builder.addCase(refundable.rejected, (state, action) => {
      state.isLoading = false
      state.error = action.error.message || "Refundable Error"
      state.promocode = null
    })
    builder.addCase(getTravellerAddress.fulfilled, (state, action) => {
      state.isLoading = false
      state.travellerInfo = {
        ...state.travellerInfo,
        address: action.payload?.bookingData?.bookingAddress.address,
        city: action.payload?.bookingData?.bookingAddress.city,
        state: action.payload?.bookingData?.bookingAddress.state,
        zipCode: action.payload?.bookingData?.bookingAddress.zipCode,
        country: action.payload?.bookingData?.bookingAddress.country,
      }
      state.initialTravellerInfo = {
        address: action.payload?.bookingData?.bookingAddress.address,
        city: action.payload?.bookingData?.bookingAddress.city,
        state: action.payload?.bookingData?.bookingAddress.state,
        zipCode: action.payload?.bookingData?.bookingAddress.zipCode,
        country: action.payload?.bookingData?.bookingAddress.country,
      }
    })
    builder.addCase(getTravellerAddress.rejected, (state, action) => {
      state.isLoading = false
      state.error = action.error.message || "TravellerAddress Error"
      state.promocode = null
    })
    builder.addCase(registerViewDetailPageEventMixpanel.fulfilled, (state) => {
      state.isPreqouteLoaded = false // so now mixpanel won't trigger
    })
  },
})

export const {
  markBookingStore,
  resetBookingStore,
  clearPrecisionPricingData,
  setPrecisionPrincingData,
  setIsPrecisionPricingProcessComplete,
} = bookingSlice.actions

export const selectBookingStore = (state: RootState) => state.bookingReducer

export const selectCancelationPolicyFromPrequote = (state: RootState) =>
  state.bookingReducer.prequote?.cancellationPolicy

export const selectPriceChangeMessage = (state: RootState) =>
  state.bookingReducer.prequote?.priceChangeMessage || ""

export const selectDailyAvgRent = (state: RootState) =>
  state.bookingReducer.prequote?.dailyAvgRent || 0

export const selectDaysLength = (state: RootState) =>
  state.bookingReducer.prequote?.days?.length || 1

export const selectHostFees = (state: RootState) =>
  state.bookingReducer.prequote?.cleaningFee || 0

export const selectTotalTaxes = (state: RootState) =>
  state.bookingReducer.prequote?.totalTax || 0

export const selectPdpFees = (state: RootState) =>
  state.bookingReducer.prequote?.pdpFees || 0

export const selectServiceFees = (state: RootState) =>
  (state.bookingReducer.prequote?.originalServiceFee || 0) +
  (state.bookingReducer.prequote?.pdpFees || 0)

export const selectTotalSavePercent = (state: RootState) =>
  state.bookingReducer.prequote?.totalSavePercent

export const selectTotalIncentivePercent = (state: RootState) =>
  state.bookingReducer.prequote?.totalIncentivePercent

export const selectTravelProtectionCost = (state: RootState) => {
  let total = 0
  total =
    (state.bookingReducer.totalAmt || 0) +
      (state.bookingReducer.prequote?.pdpFees || 0) -
      (state.bookingReducer.discount ?? 0) <=
    0
      ? 0
      : (((state.bookingReducer.totalAmt || 0) +
          (state.bookingReducer.prequote?.pdpFees || 0) -
          (state.bookingReducer.achProcessingSavings || 0) -
          (state.bookingReducer.discount ?? 0)) *
          Number(state.bookingReducer.refundable)) /
        100
  return total
}

export const selectBookingCancelTravelProtectionCost = (state: RootState) =>
  (state.bookingReducer.totalAmt || 0) - (state.bookingReducer.discount || 0) <=
  0
    ? 0
    : (((state.bookingReducer.totalAmt || 0) +
        (state.bookingReducer.prequote?.pdpFees || 0) -
        (state.bookingReducer.achProcessingSavings || 0) -
        (state.bookingReducer.discount || 0)) *
        Number(state.bookingReducer.CFAR)) /
      100

export const selectStandardTravelProtectionCost = (state: RootState) =>
  (state.bookingReducer.totalAmt || 0) - (state.bookingReducer.discount || 0) <=
  0
    ? 0
    : (((state.bookingReducer.totalAmt || 0) +
        (state.bookingReducer.prequote?.pdpFees || 0) -
        (state.bookingReducer.achProcessingSavings || 0) -
        (state.bookingReducer.discount || 0)) *
        Number(state.bookingReducer.TI)) /
      100

export const selectTotalIncludingTaxes = (state: RootState) =>
  state.bookingReducer.isTravelProtected
    ? (state.bookingReducer.totalAmt || 0) -
        (state.bookingReducer.discount || 0) <=
      0
      ? 0
      : (state.bookingReducer.totalAmt || 0) -
        (state.bookingReducer.discount || 0) +
        (((state.bookingReducer.totalAmt || 0) +
          (state.bookingReducer.prequote?.pdpFees || 0) -
          (state.bookingReducer.discount || 0)) *
          Number(state.bookingReducer.refundable)) /
          100
    : (state.bookingReducer.totalAmt || 0) -
        (state.bookingReducer.discount || 0) <
      0
    ? 0
    : (state.bookingReducer.totalAmt || 0) -
      (state.bookingReducer.discount ?? 0)

// same logic is used in loadPrecisionPricing.fulfilled.
// if anything updates here, please update there as well
export const selectTotalExcludingTaxes = (state: RootState) =>
  (state.bookingReducer.totalAmt || 0) -
    (state.bookingReducer.prequote?.totalTax || 0) >
  0
    ? state.bookingReducer.isTravelProtected
      ? (state.bookingReducer.totalAmt || 0) +
        (state.bookingReducer.prequote?.pdpFees || 0) +
        ((state.bookingReducer.totalAmt || 0) *
          Number(state.bookingReducer.refundable)) /
          100 -
        (state.bookingReducer.prequote?.totalTax || 0)
      : (state.bookingReducer.totalAmt || 0) +
        (state.bookingReducer.prequote?.pdpFees || 0) -
        (state.bookingReducer.prequote?.totalTax || 0)
    : 0

export const selectBookingCalendarRateList = (state: RootState) =>
  state.bookingReducer.calendarRateList
export const selectTravellerInfo = (state: RootState) =>
  state.bookingReducer.travellerInfo
export const selectInitialTravellerInfo = (state: RootState) =>
  state.bookingReducer.initialTravellerInfo
export const selectPrecisionPricing = (state: RootState) =>
  state.bookingReducer.precisionPricing
export const selectIsPrecisionPricingProcessComplete = (state: RootState) =>
  state.bookingReducer.isPrecisionPricingProcessComplete
export const selectPrecisionPricingAllowedDays = (state: RootState) =>
  state.bookingReducer.precisionPricingAllowedDays

export const selectMaxCancellationFee = (state: RootState) =>
  state.bookingReducer.maxCancellationFee

export default bookingSlice.reducer
